import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, forwardRef, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms';
import { getRandomId } from 'src/app/shared/extensions/string.extensions';
import { TranslationService } from 'src/app/shared/transloco/services/translation.service';
import { TimespanHelper } from './timespan.helper';
import { ITimespan } from './timespan.interface';

@Component({
    selector: 'app-standard-timespan',
    templateUrl: './standard-timespan.component.html',
    styleUrls: ['./standard-timespan.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => StandardTimespanComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => StandardTimespanComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StandardTimespanComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
    private decimalPipe = inject(DecimalPipe);

    private translationService = inject(TranslationService);

    @Input() public disabled = false;
    @Input() public label: string;
    @Input() public hint: string;
    @Input() public minValue: string;
    @Input() public maxValue: string;

    public id: string;
    public numberFormat = '2.0-0';
    public minHours = 0;
    public maxHours = 99;

    public form = new FormGroup({
        hours: new FormControl('0', [Validators.required, Validators.min(this.minHours), Validators.max(this.maxHours)]),
        minutes: new FormControl('0', [Validators.required, Validators.min(0), Validators.max(59)]),
        seconds: new FormControl('0', [Validators.required, Validators.min(0), Validators.max(59)]),
    });

    private formChanges$ = this.form.valueChanges;
    private subscription = this.formChanges$.subscribe((_) => {
        this.onChange();
    });

    constructor() {
        this.id = getRandomId();
    }

    public ngOnInit(): void {
        this.initializeLimits();
        this.form.addValidators([this.minTimespanValidator(), this.maxTimespanValidator()]);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public writeValue(timespanString: string): void {
        const timespan = TimespanHelper.fromString(timespanString);

        this.form.patchValue({
            hours: this.decimalPipe.transform(timespan.Hours, this.numberFormat),
            minutes: this.decimalPipe.transform(timespan.Minutes, this.numberFormat),
            seconds: this.decimalPipe.transform(timespan.Seconds, this.numberFormat),
        });
    }

    public onChange() {
        if (this.onChangeFn) {
            const timespan: ITimespan = {
                Hours: this.hoursControl.value,
                Minutes: this.minutesControl.value,
                Seconds: this.secondsControl.value,
            };
            const timespanString = TimespanHelper.toString(timespan);

            this.onChangeFn(timespanString);
            this.onValidationChangeFn(this.form.valid);
        }
    }

    public onChangeFn = (_: string) => {
        return;
    };

    public onTouchedFn = () => {
        return;
    };

    public registerOnChange(fn: (updatedTimespan: string) => void): void {
        this.onChangeFn = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    public setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public onValidationChangeFn = (_: boolean) => {
        return;
    };

    public validate(_: AbstractControl): ValidationErrors {
        return this.form.valid ? null : { invalidTimespan: true };
    }

    public registerOnValidatorChange?(fn: (isValid: boolean) => void): void {
        this.onValidationChangeFn = fn;
    }

    public minTimespanValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            const form = control as FormGroup;
            if (!this.minValue) {
                return;
            }

            const selectedTimeSpan = `${form.value.hours ?? 0}:${form.value.minutes ?? 0}:${form.value.seconds ?? 0}`;
            const isBelowMinimumValue = TimespanHelper.isGreater(this.minValue, selectedTimeSpan);
            if (!isBelowMinimumValue) {
                return null;
            }

            return { isBelowMinTimespan: this.translationService.translate('defaults.validation.isBelowMinTimespan', { maxValue: this.maxValue }) };
        };
    }

    public maxTimespanValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors => {
            const form = control as FormGroup;
            if (!this.maxValue) {
                return;
            }

            const selectedTimeSpan = `${form.value.hours ?? 0}:${form.value.minutes ?? 0}:${form.value.seconds ?? 0}`;
            const isAboveMaxValue = TimespanHelper.isGreater(selectedTimeSpan, this.maxValue);
            if (!isAboveMaxValue) {
                return null;
            }

            return { isAboveMaxTimespan: this.translationService.translate('defaults.validation.isAboveMaxTimespan', { maxValue: this.maxValue }) };
        };
    }

    public get hoursControl(): FormControl {
        return this.form.controls['hours'] as FormControl;
    }

    public get minutesControl(): FormControl {
        return this.form.controls['minutes'] as FormControl;
    }

    public get secondsControl(): FormControl {
        return this.form.controls['seconds'] as FormControl;
    }

    private initializeLimits(): void {
        if (this.minValue) {
            const minTimespan = TimespanHelper.fromString(this.minValue);
            this.minHours = minTimespan.Hours;
        }

        if (this.maxValue) {
            const maxTimespan = TimespanHelper.fromString(this.maxValue);
            this.maxHours = maxTimespan.Hours;
        }
    }
}
